import { useState, useEffect } from "react";
import PlatformBrand from "./PlatformBrand";
import { Link, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { List, SignOut, Sun, Moon, X, Star } from "@phosphor-icons/react";
import { haptic } from "../lib/haptics";
import { visibleItemsFor, flattenItems, itemIsActive } from "./navSections";

/** Mobile burger drawer that exposes the FULL sidebar — same nav sections as
 * desktop, just slid-in from the left. Mounted only on mobile (md:hidden).
 *
 * Props:
 *   sections     — same NAV_SECTIONS array from Layout.js
 *   user         — current user (for role filtering)
 *   counts       — { unreadCount, vendorMsgUnread, expensesPending }
 *   theme        — current theme string ("dark"|"light")
 *   onToggleTheme
 *   onLogout
 */
export default function MobileNav({ sections, favourites = [], user, counts = {}, theme, onToggleTheme, onLogout }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const favItems = favourites.map((to) => sections.flatMap((s) => flattenItems(visibleItemsFor(s, user))).find((i) => i.to === to)).filter(Boolean);

  // Listen for the swipe-from-left-edge gesture (dispatched by useSwipeBack
  // when there's no "back" target). Provides a native-feeling drawer pull
  // on root pages like the dashboard.
  useEffect(() => {
    const onOpenRequested = () => {
      haptic("light");
      setOpen(true);
    };
    window.addEventListener("necos:open-mobile-nav", onOpenRequested);
    return () => window.removeEventListener("necos:open-mobile-nav", onOpenRequested);
  }, []);

  const close = () => setOpen(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="md:hidden -ml-2 p-2 rounded-md hover:bg-muted active:scale-95 transition-transform"
          aria-label="Open menu"
          data-testid="mobile-burger-btn"
        >
          <List size={22} />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] p-0 flex flex-col" data-testid="mobile-burger-drawer">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 h-14 border-b border-border shrink-0">
          <Link to="/" onClick={close} className="flex items-center gap-2 min-w-0">
            <PlatformBrand variant="sidebar" />
          </Link>
          <button onClick={close} className="p-1.5 rounded-md hover:bg-muted" aria-label="Close menu" data-testid="mobile-burger-close">
            <X size={18} />
          </button>
        </div>

        {/* User chip */}
        {user && (
          <div className="px-4 py-3 border-b border-border bg-muted/40">
            <p className="text-sm font-semibold truncate">{user.name || "You"}</p>
            <p className="text-[11px] text-muted-foreground truncate">{(user.role || "").replace(/_/g, " ")}{user.email ? ` · ${user.email}` : ""}</p>
          </div>
        )}

        {/* Nav sections */}
        <ScrollArea className="flex-1">
          <nav className="flex flex-col gap-0.5 p-2">
            {favItems.length > 0 && (
              <div data-testid="mobile-nav-favourites">
                <div className="flex items-center gap-2 px-3 pt-2 pb-1.5">
                  <Star size={11} weight="fill" className="text-amber-500" />
                  <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-muted-foreground/60">Favourites</span>
                  <div className="flex-1 h-px bg-border/40" />
                </div>
                {favItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = itemIsActive(item, location);
                  return (
                    <Link key={`fav-${item.to}`} to={item.to} onClick={close} data-testid={`mobile-nav-fav-${item.label.toLowerCase().replace(/\s/g, "-").replace(/&/g, "and")}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors active:scale-[0.98] ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                      <Icon size={20} weight={isActive ? "fill" : "regular"} />
                      <span className="flex-1">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
            {sections.map((section) => {
              const visibleItems = flattenItems(visibleItemsFor(section, user));
              if (visibleItems.length === 0) return null;

              return (
                <div key={section.id}>
                  {section.label && (
                    <div className="flex items-center gap-2 px-3 pt-4 pb-1.5">
                      <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-muted-foreground/60">{section.label}</span>
                      <div className="flex-1 h-px bg-border/40" />
                    </div>
                  )}
                  {visibleItems.map((item) => {
                    const isActive = itemIsActive(item, location);
                    const Icon = item.icon;
                    const badge =
                      item.badge === "notifications" && counts.unreadCount > 0 ? counts.unreadCount :
                      item.badge === "messages" && counts.vendorMsgUnread > 0 ? counts.vendorMsgUnread :
                      item.badge === "expenses" && counts.expensesPending > 0 ? counts.expensesPending : null;
                    return (
                      <Link
                        key={`${section.id}-${item.to}`}
                        to={item.to}
                        onClick={close}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors active:scale-[0.98]
                          ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        <Icon size={20} weight={isActive ? "fill" : "regular"} />
                        <span className="flex-1">{item.label}</span>
                        {badge && (
                          <Badge variant="destructive" className="h-5 min-w-5 flex items-center justify-center text-[10px] px-1">{badge}</Badge>
                        )}
                      </Link>
                    );
                  })}
                </div>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Footer actions */}
        <div className="border-t border-border p-2 shrink-0 space-y-1">
          <Button variant="ghost" size="sm" className="w-full justify-start gap-3" onClick={() => { onToggleTheme?.(); }} data-testid="mobile-theme-toggle">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            <span className="text-xs">{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start gap-3 text-destructive hover:text-destructive" onClick={() => { close(); onLogout?.(); }} data-testid="mobile-logout">
            <SignOut size={18} />
            <span className="text-xs">Logout</span>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
